import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ImagePlus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { isSafeHref, type SectionNode, type WebsiteDefinition } from "@baze/schema";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/select";
import { SwitchRow } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { formatRupiahDigits, parseRupiahInput } from "@/lib/format";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import { updateContent } from "./model/ops";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type Img = { src: string; alt: string; assetId?: string; width?: number; height?: number };
type Cta = { label: string; kind: "whatsapp" | "link"; href?: string; message?: string };

export function SectionForm({ siteId, def, section, apply }: { siteId: string; def: WebsiteDefinition; section: SectionNode; apply: Apply }) {
  const set = (path: string, value: unknown) => apply(updateContent(def, section.id, path, value), `${section.id}:${path}`);

  if (section.type === "navbar") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <SwitchRow id="nav-cta" label={t.editor.fields.showCta} checked={c.showCta !== false} onCheckedChange={(v) => set("showCta", v)} />
        <TextField label={t.editor.fields.ctaLabel} value={c.ctaLabel ?? ""} optional onChange={(v) => set("ctaLabel", v || undefined)} />
      </div>
    );
  }
  if (section.type === "hero") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ImageField siteId={siteId} label={t.editor.fields.image} value={c.image} onChange={(v) => set("image", v)} />
        <CtaField label={t.editor.fields.primaryCta} value={c.primaryCta} onChange={(v) => set("primaryCta", v)} />
        <CtaField label={t.editor.fields.secondaryCta} value={c.secondaryCta} onChange={(v) => set("secondaryCta", v)} />
      </div>
    );
  }
  if (section.type === "about") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <TextField label={t.editor.fields.body} value={c.body} multiline max={1500} onChange={(v) => set("body", v)} />
        <ImageField siteId={siteId} label={t.editor.fields.image} value={c.image} onChange={(v) => set("image", v)} />
        <StringList label={t.editor.fields.highlights} itemLabel={t.editor.fields.highlight} items={c.highlights ?? []} onChange={(items) => set("highlights", items.length ? items : undefined)} />
      </div>
    );
  }
  if (section.type === "services") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.items}
          items={c.items}
          blank={() => ({ title: "Layanan baru" })}
          titleOf={(item) => item.title}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.itemTitle} value={item.title} onChange={(v) => update({ title: v || item.title })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <PriceField value={item.price} onChange={(v) => update({ price: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "products") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.items}
          items={c.items}
          blank={() => ({ name: "Produk baru" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.itemName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <PriceField value={item.price} onChange={(v) => update({ price: v })} />
              <TextField
                label={t.editor.fields.priceNote}
                value={item.priceNote ?? ""}
                optional
                placeholder={t.editor.fields.priceNotePlaceholder}
                onChange={(v) => update({ priceNote: v || undefined })}
              />
              <TextField label={t.editor.fields.badge} value={item.badge ?? ""} optional onChange={(v) => update({ badge: v || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
              <details className="rounded-lg bg-white ring-1 ring-slate-900/8" open={!!(item.artist || item.medium || item.size || item.year || item.status)}>
                <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700">{t.editor.fields.artworkDetails}</summary>
                <div className="space-y-3 px-3 pb-3">
                  <TextField label={t.editor.fields.artist} value={item.artist ?? ""} optional onChange={(v) => update({ artist: v || undefined })} />
                  <TextField
                    label={t.editor.fields.medium}
                    value={item.medium ?? ""}
                    optional
                    placeholder={t.editor.fields.mediumPlaceholder}
                    onChange={(v) => update({ medium: v || undefined })}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <TextField label={t.editor.fields.size} value={item.size ?? ""} optional placeholder="90 × 120 cm" onChange={(v) => update({ size: v || undefined })} />
                    <TextField label={t.editor.fields.year} value={item.year ?? ""} optional placeholder="2025" onChange={(v) => update({ year: v || undefined })} />
                  </div>
                  <Field label={t.editor.fields.status} optional>
                    <NativeSelect
                      selectSize="sm"
                      value={item.status ?? ""}
                      onChange={(e) => update({ status: (e.target.value || undefined) as typeof item.status })}
                    >
                      <option value="">{t.editor.fields.statusNone}</option>
                      <option value="available">{t.editor.fields.statusAvailable}</option>
                      <option value="reserved">{t.editor.fields.statusReserved}</option>
                      <option value="sold">{t.editor.fields.statusSold}</option>
                    </NativeSelect>
                  </Field>
                </div>
              </details>
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "menu") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.categories}
          items={c.categories}
          blank={() => ({ name: "Kategori", items: [{ name: "Menu baru" }] })}
          titleOf={(item) => item.name}
          onChange={(items) => set("categories", items)}
          render={(cat, update) => (
            <>
              <TextField label={t.editor.fields.categoryName} value={cat.name} onChange={(v) => update({ name: v || cat.name })} />
              <ItemStack
                label={t.editor.fields.menuItems}
                items={cat.items}
                blank={() => ({ name: "Menu baru" })}
                titleOf={(item) => item.name}
                onChange={(items) => update({ items })}
                render={(item, updateItem) => (
                  <>
                    <TextField label={t.editor.fields.itemName} value={item.name} onChange={(v) => updateItem({ name: v || item.name })} />
                    <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional onChange={(v) => updateItem({ description: v || undefined })} />
                    <PriceField value={item.price} onChange={(v) => updateItem({ price: v })} />
                  </>
                )}
              />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "gallery") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <div className="space-y-2">
          {c.images.map((image, index) => (
            <ImageField key={image.src + index} siteId={siteId} label={t.editor.inspector.itemN(t.editor.fields.photo, index + 1)} value={image} onChange={(v) => set("images", v ? c.images.map((img, i) => (i === index ? v : img)) : c.images.filter((_, i) => i !== index))} />
          ))}
          <ImageField siteId={siteId} label={t.editor.fields.images} onChange={(v) => { if (v) set("images", [...c.images, v]); }} />
        </div>
      </div>
    );
  }
  if (section.type === "testimonials") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.items}
          items={c.items}
          blank={() => ({ quote: "Ulasan pelanggan.", name: "Nama pelanggan" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              {item.sample ? <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">{t.editor.fields.sampleWarning}</p> : null}
              <TextField label={t.editor.fields.quote} value={item.quote} multiline onChange={(v) => update({ quote: v || item.quote, sample: undefined })} />
              <TextField label={t.editor.fields.name} value={item.name} onChange={(v) => update({ name: v || item.name, sample: undefined })} />
              <TextField label={t.editor.fields.role} value={item.role ?? ""} optional onChange={(v) => update({ role: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "faq") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.items}
          items={c.items}
          blank={() => ({ question: "Pertanyaan baru", answer: "Jawaban singkat." })}
          titleOf={(item) => item.question}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.question} value={item.question} onChange={(v) => update({ question: v || item.question })} />
              <TextField label={t.editor.fields.answer} value={item.answer} multiline onChange={(v) => update({ answer: v || item.answer })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "stats") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.items}
          items={c.items}
          blank={() => ({ value: "0", label: "Keterangan" })}
          titleOf={(item) => item.label}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.value} value={item.value} onChange={(v) => update({ value: v || item.value })} />
              <TextField label={t.editor.fields.label} value={item.label} onChange={(v) => update({ label: v || item.label })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "opening-hours") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <TextField label={t.editor.fields.note} value={c.note ?? ""} optional multiline onChange={(v) => set("note", v || undefined)} />
        <p className="text-[13px] text-slate-500">{t.editor.inspector.fromBusinessInfo}</p>
      </div>
    );
  }
  if (section.type === "location") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="show-map" label={t.editor.fields.showMap} checked={c.showMap !== false} onCheckedChange={(v) => set("showMap", v)} />
        <p className="text-[13px] text-slate-500">{t.editor.inspector.fromBusinessInfo}</p>
      </div>
    );
  }
  if (section.type === "contact") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <TextField label={t.editor.fields.message} value={c.message ?? ""} optional multiline onChange={(v) => set("message", v || undefined)} />
        <SwitchRow id="show-wa" label={t.editor.fields.showWhatsapp} checked={c.showWhatsapp !== false} onCheckedChange={(v) => set("showWhatsapp", v)} />
        <SwitchRow id="show-phone" label={t.editor.fields.showPhone} checked={c.showPhone !== false} onCheckedChange={(v) => set("showPhone", v)} />
        <SwitchRow id="show-email" label={t.editor.fields.showEmail} checked={c.showEmail !== false} onCheckedChange={(v) => set("showEmail", v)} />
        <SwitchRow id="show-social" label={t.editor.fields.showSocial} checked={c.showSocial !== false} onCheckedChange={(v) => set("showSocial", v)} />
      </div>
    );
  }
  if (section.type === "pricing") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.plans}
          items={c.plans}
          blank={() => ({ name: "Paket baru", features: ["Fitur utama"] })}
          titleOf={(item) => item.name}
          onChange={(items) => set("plans", items)}
          render={(plan, update) => (
            <>
              <TextField label={t.editor.fields.planName} value={plan.name} onChange={(v) => update({ name: v || plan.name })} />
              <PriceField value={plan.price} onChange={(v) => update({ price: v })} />
              <TextField label={t.editor.fields.period} value={plan.period ?? ""} optional onChange={(v) => update({ period: v || undefined })} />
              <StringList label={t.editor.fields.features} itemLabel={t.editor.fields.feature} items={plan.features} onChange={(features) => update({ features: features.length ? features : ["Fitur utama"] })} />
              <SwitchRow id={`plan-hi-${plan.name}`} label={t.editor.fields.highlighted} checked={!!plan.highlighted} onCheckedChange={(v) => update({ highlighted: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "cta") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <CtaField label={t.editor.fields.button} value={c.button} required onChange={(v) => { if (v) set("button", v); }} />
      </div>
    );
  }
  if (section.type === "timeline") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.steps}
          items={c.items}
          blank={() => ({ title: "Langkah baru" })}
          titleOf={(item) => item.title}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.stepLabel} value={item.label ?? ""} optional onChange={(v) => update({ label: v || undefined })} />
              <TextField label={t.editor.fields.itemTitle} value={item.title} onChange={(v) => update({ title: v || item.title })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "events") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="ev-cal" label={t.editor.fields.showCalendar} checked={c.showCalendar !== false} onCheckedChange={(v) => set("showCalendar", v)} />
        <ItemStack
          label={t.editor.fields.events}
          items={c.items}
          blank={() => ({ name: "Acara baru" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.eventName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <Field label={t.editor.fields.date} optional>
                <Input type="date" value={item.date ?? ""} onChange={(e) => update({ date: e.target.value || undefined })} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t.editor.fields.startTime} optional>
                  <Input type="time" value={item.startTime ?? ""} onChange={(e) => update({ startTime: e.target.value || undefined })} />
                </Field>
                <Field label={t.editor.fields.endTime} optional>
                  <Input type="time" value={item.endTime ?? ""} onChange={(e) => update({ endTime: e.target.value || undefined })} />
                </Field>
              </div>
              <TextField label={t.editor.fields.timeNote} value={item.timeNote ?? ""} optional onChange={(v) => update({ timeNote: v || undefined })} />
              <TextField label={t.editor.fields.venue} value={item.venue ?? ""} optional onChange={(v) => update({ venue: v || undefined })} />
              <TextField label={t.editor.fields.address} value={item.address ?? ""} optional multiline onChange={(v) => update({ address: v || undefined })} />
              <TextField label={t.editor.fields.mapUrl} value={item.mapUrl ?? ""} optional onChange={(v) => update({ mapUrl: v.trim() || undefined })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "team") {
    const c = section.content;
    // The "couple" variant is a wedding's bride and groom: name, "Putri dari", and the parents.
    const couple = section.variant === "couple";
    const f = t.editor.fields;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={couple ? f.couple : f.people}
          items={c.items}
          blank={() => ({ name: couple ? f.coupleName : "Nama" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={couple ? f.coupleName : f.name} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <TextField label={couple ? f.coupleRole : f.personRole} value={item.role ?? ""} optional onChange={(v) => update({ role: v || undefined })} />
              <TextField label={couple ? f.coupleParents : f.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <TextField label={t.editor.fields.instagram} value={item.instagram ?? ""} optional onChange={(v) => update({ instagram: v.replace(/^@/, "").replace(/[^A-Za-z0-9._]/g, "").slice(0, 30) || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.photo} value={item.image} onChange={(v) => update({ image: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "quote") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <TextField label={t.editor.fields.eyebrow} value={c.eyebrow ?? ""} optional onChange={(v) => set("eyebrow", v || undefined)} />
        <TextField label={t.editor.fields.quoteText} value={c.text} multiline max={600} error={!c.text.trim() ? t.editor.requiredField : undefined} onChange={(v) => set("text", v)} />
        <TextField label={t.editor.fields.cite} value={c.cite ?? ""} optional onChange={(v) => set("cite", v || undefined)} />
      </div>
    );
  }
  if (section.type === "gift") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <ItemStack
          label={t.editor.fields.accounts}
          items={c.accounts}
          blank={() => ({ provider: "Nama bank", name: "Nama pemilik", number: "0000000000" })}
          titleOf={(item) => item.provider}
          onChange={(items) => set("accounts", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.provider} value={item.provider} onChange={(v) => update({ provider: v || item.provider })} />
              <TextField label={t.editor.fields.accountNumber} value={item.number} onChange={(v) => update({ number: v.replace(/[^0-9A-Za-z .+-]/g, "").slice(0, 40) || item.number })} />
              <TextField label={t.editor.fields.accountName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
            </>
          )}
        />
        <TextField label={t.editor.fields.giftAddress} value={c.address ?? ""} optional multiline onChange={(v) => set("address", v || undefined)} />
      </div>
    );
  }
  if (section.type === "links") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title ?? ""} titleOptional onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} />
        <ItemStack
          label={t.editor.fields.links}
          items={c.items}
          blank={() => ({ label: "Tautan baru", href: "https://example.com" })}
          titleOf={(item) => item.label}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.linkLabel} value={item.label} onChange={(v) => update({ label: v || item.label })} />
              <UrlField value={item.href} onChange={(v) => update({ href: v })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional onChange={(v) => update({ description: v || undefined })} />
              <SwitchRow id={`link-hi-${item.label}`} label={t.editor.fields.linkHighlight} checked={!!item.highlight} onCheckedChange={(v) => update({ highlight: v || undefined })} />
              <SwitchRow id={`link-sp-${item.label}`} label={t.editor.fields.linkSponsored} checked={!!item.sponsored} onCheckedChange={(v) => update({ sponsored: v || undefined })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "picks") {
    const c = section.content;
    const affiliate = c.affiliate !== false;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="picks-aff" label={t.editor.fields.affiliate} checked={affiliate} onCheckedChange={(v) => set("affiliate", v ? undefined : false)} />
        {affiliate ? (
          <TextField label={t.editor.fields.disclosure} value={c.disclosure ?? ""} optional multiline onChange={(v) => set("disclosure", v || undefined)} />
        ) : null}
        <TextField label={t.editor.fields.pickButton} value={c.linkLabel ?? ""} optional onChange={(v) => set("linkLabel", v || undefined)} />
        <ItemStack
          label={t.editor.fields.items}
          items={c.items}
          blank={() => ({ name: "Produk baru", href: "https://example.com" })}
          titleOf={(item) => item.name}
          onChange={(items) => set("items", items)}
          render={(item, update) => (
            <>
              <TextField label={t.editor.fields.itemName} value={item.name} onChange={(v) => update({ name: v || item.name })} />
              <UrlField value={item.href} onChange={(v) => update({ href: v })} />
              <TextField label={t.editor.fields.store} value={item.store ?? ""} optional onChange={(v) => update({ store: v || undefined })} />
              <TextField label={t.editor.fields.itemDescription} value={item.description ?? ""} optional multiline onChange={(v) => update({ description: v || undefined })} />
              <PriceField value={item.price} onChange={(v) => update({ price: v })} />
              <TextField label={t.editor.fields.badge} value={item.badge ?? ""} optional onChange={(v) => update({ badge: v || undefined })} />
              <ImageField siteId={siteId} label={t.editor.fields.image} value={item.image} onChange={(v) => update({ image: v })} />
            </>
          )}
        />
      </div>
    );
  }
  if (section.type === "blog-latest") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <Field label={t.editor.fields.postCount}>
          <NativeSelect selectSize="sm" value={String(c.count ?? 3)} onChange={(e) => set("count", Number(e.target.value))}>
            {[3, 6].map((n) => (
              <option key={n} value={n}>
                {t.editor.fields.postCountOption(n)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <TextField label={t.editor.fields.postLinkLabel} value={c.linkLabel ?? ""} optional onChange={(v) => set("linkLabel", v || undefined)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.blogLatestNote}</p>
      </div>
    );
  }
  if (section.type === "wishes") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <SwitchRow id="wish-att" label={t.editor.fields.askAttendance} checked={!!c.askAttendance} onCheckedChange={(v) => set("askAttendance", v || undefined)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.wishesNote}</p>
      </div>
    );
  }
  if (section.type === "rsvp") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <Field label={t.editor.fields.rsvpDeadline} optional>
          <Input type="date" value={c.deadline ?? ""} onChange={(e) => set("deadline", e.target.value || undefined)} />
        </Field>
        <Field label={t.editor.fields.maxPax}>
          <NativeSelect selectSize="sm" value={String(c.maxPax ?? 2)} onChange={(e) => set("maxPax", Number(e.target.value))}>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {t.editor.fields.paxOption(n)}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <SwitchRow id="rsvp-note" label={t.editor.fields.askNote} checked={!!c.askNote} onCheckedChange={(v) => set("askNote", v || undefined)} />
        <p className="text-[13px] leading-relaxed text-slate-500">{t.editor.fields.rsvpNote}</p>
      </div>
    );
  }
  if (section.type === "countdown") {
    const c = section.content;
    return (
      <div className="space-y-3">
        <Heading eyebrow={c.eyebrow} title={c.title} description={c.description} onEyebrow={(v) => set("eyebrow", v)} onTitle={(v) => set("title", v)} onDescription={(v) => set("description", v)} />
        <Field label={t.editor.fields.date}>
          <Input type="date" value={c.date} onChange={(e) => e.target.value && set("date", e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t.editor.fields.startTime} optional>
            <Input type="time" value={c.time ?? ""} onChange={(e) => set("time", e.target.value || undefined)} />
          </Field>
          <Field label={t.editor.fields.zone} optional>
            <NativeSelect selectSize="sm" value={c.timeNote ?? "WIB"} onChange={(e) => set("timeNote", e.target.value)}>
              <option value="WIB">WIB</option>
              <option value="WITA">WITA</option>
              <option value="WIT">WIT</option>
            </NativeSelect>
          </Field>
        </div>
        <TextField label={t.editor.fields.doneText} value={c.doneText ?? ""} optional onChange={(v) => set("doneText", v || undefined)} />
        <SwitchRow id="cd-cal" label={t.editor.fields.showCalendar} checked={c.showCalendar !== false} onCheckedChange={(v) => set("showCalendar", v)} />
      </div>
    );
  }
  const c = section.content;
  return (
    <div className="space-y-3">
      <TextField label={t.editor.fields.footerText} value={c.text ?? ""} optional multiline onChange={(v) => set("text", v || undefined)} />
      <SwitchRow id="foot-social" label={t.editor.fields.showSocial} checked={c.showSocial !== false} onCheckedChange={(v) => set("showSocial", v)} />
      <SwitchRow id="foot-hours" label={t.editor.fields.showHours} checked={c.showHours !== false} onCheckedChange={(v) => set("showHours", v)} />
      <SwitchRow id="foot-address" label={t.editor.fields.showAddress} checked={c.showAddress !== false} onCheckedChange={(v) => set("showAddress", v)} />
    </div>
  );
}

function Heading({
  eyebrow,
  title,
  description,
  titleOptional,
  onEyebrow,
  onTitle,
  onDescription,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  titleOptional?: boolean;
  onEyebrow: (v: string | undefined) => void;
  onTitle: (v: string | undefined) => void;
  onDescription?: (v: string | undefined) => void;
}) {
  return (
    <>
      <TextField label={t.editor.fields.eyebrow} value={eyebrow ?? ""} optional onChange={(v) => onEyebrow(v || undefined)} />
      <TextField label={t.editor.fields.title} value={title} optional={titleOptional} error={!titleOptional && !title.trim() ? t.editor.requiredField : undefined} onChange={(v) => onTitle(titleOptional ? v || undefined : v)} />
      {onDescription ? <TextField label={t.editor.fields.description} value={description ?? ""} optional multiline onChange={(v) => onDescription(v || undefined)} /> : null}
    </>
  );
}

function TextField({
  label,
  value,
  onChange,
  multiline,
  optional,
  max,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  optional?: boolean;
  max?: number;
  error?: string;
  placeholder?: string;
}) {
  return (
    <Field label={label} optional={optional} error={error} count={max ? { value: value.length, max } : undefined}>
      {multiline ? (
        <Textarea value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={4} />
      ) : (
        <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </Field>
  );
}

/**
 * Link input. "shopee.co.id/x" becomes "https://shopee.co.id/x". Only valid, safe links are written to the
 * definition; while the text is invalid the last good link stays live and an error is shown.
 */
function UrlField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  const normalize = (v: string) => {
    const s = v.trim();
    return s && !/^(?:[a-z][a-z0-9+.-]*:|#|\/)/i.test(s) ? `https://${s}` : s;
  };
  const valid = isSafeHref(normalize(draft));
  return (
    <Field label={t.editor.fields.url} error={valid ? undefined : t.editor.fields.urlInvalid}>
      <Input
        inputMode="url"
        value={draft}
        placeholder="https://shopee.co.id/…"
        onChange={(e) => {
          setDraft(e.target.value);
          const next = normalize(e.target.value);
          if (isSafeHref(next)) onChange(next);
        }}
      />
    </Field>
  );
}

function PriceField({ value, onChange }: { value?: number; onChange: (v: number | undefined) => void }) {
  return (
    <Field label={t.editor.fields.price} optional>
      <Input inputMode="numeric" leading={<span className="text-xs font-semibold">Rp</span>} value={formatRupiahDigits(value)} onChange={(e) => onChange(parseRupiahInput(e.target.value))} />
    </Field>
  );
}

function CtaField({ label, value, required, onChange }: { label: string; value?: Cta; required?: boolean; onChange: (v: Cta | undefined) => void }) {
  if (!value) {
    return (
      <Button variant="secondary" size="sm" onClick={() => onChange({ label: "Chat WhatsApp", kind: "whatsapp" })}>
        <Plus aria-hidden="true" />
        {label}
      </Button>
    );
  }
  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-3 ring-1 ring-slate-900/8">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{label}</p>
        {required ? null : (
          <button type="button" className="text-xs font-semibold text-slate-500 hover:text-danger" onClick={() => onChange(undefined)}>
            {t.editor.inspector.ctaRemove}
          </button>
        )}
      </div>
      <TextField label={t.editor.inspector.ctaLabel} value={value.label} onChange={(v) => onChange({ ...value, label: v || value.label })} />
      <Field label={t.editor.inspector.ctaKind}>
        <NativeSelect selectSize="sm" value={value.kind} onChange={(e) => onChange({ ...value, kind: e.target.value as Cta["kind"] })}>
          <option value="whatsapp">{t.editor.inspector.ctaKinds.whatsapp}</option>
          <option value="link">{t.editor.inspector.ctaKinds.link}</option>
        </NativeSelect>
      </Field>
      {value.kind === "link" ? (
        <TextField label={t.editor.inspector.ctaHref} value={value.href ?? ""} onChange={(v) => onChange({ ...value, href: v || undefined })} />
      ) : (
        <TextField label={t.editor.inspector.ctaMessage} value={value.message ?? ""} optional multiline onChange={(v) => onChange({ ...value, message: v || undefined })} />
      )}
    </div>
  );
}

function StringList({ label, itemLabel, items, onChange }: { label: string; itemLabel: string; items: string[]; onChange: (items: string[]) => void }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input value={item} aria-label={t.editor.inspector.itemN(itemLabel, index + 1)} onChange={(e) => onChange(items.map((v, i) => (i === index ? e.target.value || v : v)))} />
          <button type="button" className="grid size-9 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-danger-soft hover:text-danger" aria-label={t.editor.inspector.removeItem} onClick={() => onChange(items.filter((_, i) => i !== index))}>
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={() => onChange([...items, itemLabel])}>
        <Plus aria-hidden="true" />
        {t.editor.inspector.addItem}
      </Button>
    </div>
  );
}

/**
 * A list of items (people, services, accounts…). One item is open at a time so long lists stay scannable;
 * items can be reordered, and a removed item can be brought back from the toast.
 */
function ItemStack<T extends object>({
  label,
  items,
  blank,
  titleOf,
  onChange,
  render,
}: {
  label: string;
  items: T[];
  blank: () => NoInfer<T>;
  titleOf: (item: T) => string;
  onChange: (items: T[]) => void;
  render: (item: T, update: (patch: Partial<T>) => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState<number | null>(0);
  // The toast's undo runs later: put the item back into the list as it is then, not as it was.
  const latest = useRef(items);
  useEffect(() => {
    latest.current = items;
  }, [items]);
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row!);
    onChange(next);
    setOpen((cur) => (cur === from ? to : cur === to ? from : cur));
  };
  const remove = (index: number) => {
    const removed = items[index]!;
    onChange(items.filter((_, i) => i !== index));
    setOpen(null);
    toast(t.editor.inspector.itemRemoved(titleOf(removed) || t.editor.inspector.itemN(label, index + 1)), {
      action: {
        label: t.common.undo,
        onClick: () => {
          const now = latest.current;
          onChange([...now.slice(0, index), removed, ...now.slice(index)]);
          setOpen(index);
        },
      },
    });
  };
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {items.map((item, index) => {
        const expanded = open === index;
        const title = titleOf(item) || t.editor.inspector.itemN(label, index + 1);
        return (
          <div key={index} className="rounded-xl bg-slate-50 ring-1 ring-slate-900/8">
            <div className="flex items-center gap-1 py-1 pl-1 pr-2">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : index)}
                className="flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-left text-sm font-semibold text-slate-800 hover:bg-white"
              >
                <ChevronDown className={cn("size-4 shrink-0 text-slate-400 transition-transform", !expanded && "-rotate-90")} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{title}</span>
              </button>
              <button type="button" aria-label={`${t.editor.inspector.moveItemUp}: ${title}`} disabled={index === 0} onClick={() => move(index, index - 1)} className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-30">
                <ChevronUp className="size-4" aria-hidden="true" />
              </button>
              <button type="button" aria-label={`${t.editor.inspector.moveItemDown}: ${title}`} disabled={index === items.length - 1} onClick={() => move(index, index + 1)} className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-30">
                <ChevronDown className="size-4" aria-hidden="true" />
              </button>
              <button type="button" aria-label={`${t.editor.inspector.removeItem}: ${title}`} onClick={() => remove(index)} className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-danger">
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </div>
            {expanded ? <div className="space-y-3 px-3 pb-3 pt-1">{render(item, (patch) => onChange(items.map((row, i) => (i === index ? { ...row, ...patch } : row))))}</div> : null}
          </div>
        );
      })}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          onChange([...items, blank()]);
          setOpen(items.length);
        }}
      >
        <Plus aria-hidden="true" />
        {t.editor.inspector.addItem}
      </Button>
    </div>
  );
}

export function ImageField({
  siteId,
  label,
  value,
  onChange,
  fit = "cover",
  hint,
  uploaded,
}: {
  siteId: string;
  label: string;
  value?: Img;
  onChange: (v: Img | undefined) => void;
  fit?: "cover" | "contain";
  hint?: string;
  uploaded?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.assets.upload(siteId, file),
    meta: { silent: true },
    onSuccess: (asset) => {
      onChange({ src: asset.url, alt: value?.alt ?? "", assetId: asset.id, width: asset.width, height: asset.height });
      toast.success(uploaded ?? t.editor.image.uploaded);
    },
    onError: () => toast.error(t.editor.image.badType),
  });
  const pick = (file: File | undefined) => {
    if (!file) return;
    const ok = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
    if (!ok) {
      toast.error(t.editor.image.badType);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t.editor.image.tooLarge);
      return;
    }
    upload.mutate(file);
  };
  return (
    <Field label={label} optional hint={value ? undefined : (hint ?? t.editor.image.uploadHint)}>
      <div className="space-y-2">
        {value ? (
          <div className={fit === "contain" ? "grid h-24 place-items-center rounded-xl bg-slate-50 ring-1 ring-slate-900/10" : "overflow-hidden rounded-xl ring-1 ring-slate-900/10"}>
            <img src={value.src} alt="" className={fit === "contain" ? "max-h-16 max-w-[70%] object-contain" : "h-28 w-full object-cover"} />
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>
            <ImagePlus aria-hidden="true" />
            {value ? t.editor.image.change : t.editor.image.upload}
          </Button>
          {value ? (
            <Button variant="ghost" size="sm" onClick={() => onChange(undefined)}>
              {t.editor.image.remove}
            </Button>
          ) : null}
        </div>
        {value ? <Input aria-label={t.editor.image.alt} placeholder={t.editor.image.alt} value={value.alt} onChange={(e) => onChange({ ...value, alt: e.target.value })} /> : null}
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </Field>
  );
}
